<script setup lang="ts">
import { confirmFieldFileUpload } from '@/backend/confirmFieldFileUpload'
import { deleteEntity } from '@/backend/deleteEntity'
import { listEntities } from '@/backend/listEntities'
import { startFieldFileUpload } from '@/backend/startFieldFileUpload'
import { uploadFile } from '@/backend/uploadFile'
import { useLimitedAction } from '@/modules/Billing/useLimitedAction'
import type { LibraryFile } from '@/modules/Library/libraryStore'
import type { Field } from '@/modules/Project/Fields/types'
import FileUploadMenu from '@/modules/Project/FileUploadMenu.vue'
import type { Property } from '@/modules/Project/Properties/types'
import { resolveFilename } from '@/modules/Project/resolveFilename'
import { serializeEntity } from '@/modules/Project/serializer'
import { useSupportedMimeTypes } from '@/modules/Project/useSupportedMimeTypes'
import type { GoogleDriveFile } from '@/modules/Workspaces/KnowledgeHub/Integrations/GoogleDrive/useGoogleDriveConnection'
import { useIntegrationStore } from '@/modules/Workspaces/KnowledgeHub/useIntegrationStore'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { invariant } from '@/shared/utils/typeAssertions'
import { useDropzone } from '@/sharedComposables/useDropzone'
import { useLoadAllPaginatedResults } from '@/sharedComposables/useLoadAllPaginatedResults'
import BadgeItem from '@/uiKit/BadgeItem.vue'
import CircularProgress from '@/uiKit/CircularProgress.vue'
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue'

const props = defineProps<{
  property: Property<'collection'>
  filePropertyId: string
  parentEntityId: string
  childPropertyType: 'file' | 'data'
}>()

const { addEntity } = useLimitedAction()
const { workspaceId } = useWorkspaceContext()

const savedFiles = ref<
  Array<{ filename: string; entityId: string; field: Field<'file' | 'data'> }>
>([])
const loadAllChildEntities = useLoadAllPaginatedResults(
  ({ after, first }) =>
    listEntities({
      workspaceId: workspaceId.value,
      projectId: props.property.config.subprojectConfig.child_project_id,
      parent_entity_id: props.parentEntityId,
      after,
      first,
    }),
  serializeEntity,
  (entities) => {
    for (const entity of entities) {
      const field = entity.fields.get(props.filePropertyId)
      invariant(
        field?.type === 'file' || field?.type === 'data',
        'Field is not a file or data field',
      )
      const filename = resolveFilename(field)
      if (!filename) {
        continue // This is okay. The entity might not have a file uploaded to it.
      }

      savedFiles.value.push({ filename, entityId: entity.id, field })
    }
  },
)
onMounted(loadAllChildEntities)

const onDeleteEntity = async (event: Event, entityId: string) => {
  event.stopPropagation()
  const oldIndex = savedFiles.value.findIndex((file) => file.entityId === entityId)
  const oldEntry = savedFiles.value[oldIndex]
  if (!oldEntry) {
    return
  }

  savedFiles.value = savedFiles.value.filter((file) => file.entityId !== entityId)
  const res = await deleteEntity({
    entityId,
    projectId: props.property.config.subprojectConfig.child_project_id,
    workspaceId: workspaceId.value,
  })

  if (!res.ok) {
    savedFiles.value.splice(oldIndex, 0, oldEntry)
  }
}

const createEntityForUpload = async (): Promise<[string, Field<'file' | 'data'>]> => {
  const entityRes = await addEntity({
    projectId: props.property.config.subprojectConfig.child_project_id,
    workspaceId: workspaceId.value,
    parentEntityId: props.parentEntityId,
  })

  invariant(entityRes.ok)
  const entity = serializeEntity(entityRes.data)
  const field = entity.fields.get(props.filePropertyId)
  invariant(field?.type === 'file' || field?.type === 'data', 'Field is not a file or data field')
  return [entity.id, field]
}

const uploadingStatus = ref<null | {
  current: number
  total: number
}>(null)
const onUploadFiles = async (files: File[]) => {
  uploadingStatus.value = {
    current: 0,
    total: files.length,
  }
  for (const file of files) {
    const [entityId, field] = await createEntityForUpload()

    const startUploadRes = await startFieldFileUpload(
      workspaceId.value,
      props.property.config.subprojectConfig.child_project_id,
      entityId,
      props.filePropertyId,
      file.name,
    )

    invariant(startUploadRes.ok)

    const uploadRes = await uploadFile(startUploadRes.data.file_upload_url, file)
    invariant(uploadRes.ok)

    const confirmRes = await confirmFieldFileUpload(startUploadRes.data.confirm_upload_url)
    invariant(confirmRes.ok)

    savedFiles.value.push({ filename: file.name, entityId, field })
    scrollToBottomOfDropzone()

    if (uploadingStatus.value) {
      uploadingStatus.value.current++
    }
  }

  uploadingStatus.value = null
}

const dropzone = useTemplateRef('dropzone')
const scrollToBottomOfDropzone = async () => {
  await nextTick() // Wait for any new files to be shown in the DOM
  dropzone.value?.scrollTo({
    top: dropzone.value?.scrollHeight,
    behavior: 'smooth',
  })
}

const { supportedMimeTypes } = useSupportedMimeTypes(
  computed(() => props.childPropertyType ?? props.property.type),
)
const { isOverDropZone } = useDropzone(dropzone, {
  onAccepted: onUploadFiles,
  supportedMimeTypes,
  multiple: true,
})

const onImportLibraryItems = async (files: LibraryFile[]) => {
  for (const file of files) {
    const res = await addEntity({
      projectId: props.property.config.subprojectConfig.child_project_id,
      workspaceId: workspaceId.value,
      parentEntityId: props.parentEntityId,
      fields: {
        [props.filePropertyId]: {
          file_name: file.filename,
          file_url: file.fileUrl,
        },
      },
    })

    if (res.ok) {
      const entity = serializeEntity(res.data)
      const field = entity.fields.get(props.filePropertyId)
      invariant(
        field?.type === 'file' || field?.type === 'data',
        'Field is not a file or data field',
      )
      savedFiles.value.push({ filename: file.filename, entityId: res.data.id, field })
      scrollToBottomOfDropzone()
    }
  }
}

const integrationStore = useIntegrationStore()
const onImportGoogleDriveItems = async (files: GoogleDriveFile[]) => {
  const driveConnection = integrationStore.findConnection('google_drive')
  invariant(driveConnection)

  for (const file of files) {
    const res = await addEntity({
      projectId: props.property.config.subprojectConfig.child_project_id,
      workspaceId: workspaceId.value,
      parentEntityId: props.parentEntityId,
      fields: {
        [props.filePropertyId]: {
          connection_id: driveConnection.id,
          integration_id: driveConnection.integrationId,
          file_id: file.id,
          file_name: file.name,
        },
      },
    })

    if (res.ok) {
      const entity = serializeEntity(res.data)
      const field = entity.fields.get(props.filePropertyId)
      invariant(
        field?.type === 'file' || field?.type === 'data',
        'Field is not a file or data field',
      )
      savedFiles.value.push({ filename: file.name, entityId: res.data.id, field })
      scrollToBottomOfDropzone()
    }
  }
}
</script>

<template>
  <FileUploadMenu
    v-slot="{ getTriggerProps, menu }"
    library-picker-confirm-label="Upload file"
    multiple
    :supported-mime-types="supportedMimeTypes ?? null"
    :positioning="{ placement: 'bottom-start', offset: { mainAxis: 2, crossAxis: 1 } }"
    @import:computer="onUploadFiles"
    @import:library="onImportLibraryItems"
    @import:google="onImportGoogleDriveItems"
  >
    <button
      ref="dropzone"
      class="group go-scrollbar relative flex max-h-32 min-h-7 w-full max-w-full grow flex-wrap items-center justify-start gap-1 overflow-y-auto rounded-corner-8 p-1 hover:bg-background-transparent-hovered focus:bg-background-transparent-hovered focus:outline-hidden"
      aria-label="Add files"
      :class="[(menu?.open || isOverDropZone) && 'bg-background-transparent-hovered']"
      v-bind="{ ...getTriggerProps(), ...$attrs }"
    >
      <template v-if="savedFiles.length === 0 && !uploadingStatus">
        <span
          v-if="!menu?.open && !isOverDropZone"
          class="pl-1 text-text-subtle group-hover:hidden group-focus:hidden"
        >
          No file
        </span>
        <BadgeItem
          variant="neutral"
          size="sm"
          class="group-hover:opacity-100 group-focus:opacity-100"
          :class="menu?.open || isOverDropZone ? 'opacity-100' : 'opacity-0'"
          leading-icon="plus"
          label="Add files or drag and drop"
        />
      </template>

      <BadgeItem
        v-for="field in savedFiles"
        :key="field.entityId"
        :label="field.filename"
        size="sm"
        variant="neutral"
        leading-icon="attachment"
        trailing-icon="close"
        :trailing-icon-aria-label="`Remove ${field.filename}`"
        class="min-w-0"
        @trailing-icon-click="onDeleteEntity($event, field.entityId)"
      />

      <CircularProgress
        v-if="uploadingStatus"
        size="sm"
        class="absolute top-1 right-1"
        :value="uploadingStatus.current / uploadingStatus.total"
      />
    </button>
  </FileUploadMenu>
</template>
